import {
  Box,
  Button,
  Typography,
  FormControl,
  MenuItem,
  ListSubheader,
  TextField,
} from "@mui/material";
import AppDialog from "../../components/AppBase/AppDialog";
import { Fonts } from "../../constants/AppEnums";
import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { RuleInstance } from "../../types/models/RuleInstance";
import { useDispatch } from "react-redux";
import { RuleUnitState } from "../../types/models/RuleUnitState";
import { createAdminRuleOfWeek, updateAdminRuleOfWeek } from "../../redux/actions/RuleOfWeekAdmin";
import { showMessage } from "../../redux/actions/Common";
import { RuleOfWeekRequest } from "../../types/models/RuleOfWeekRequest";
import { RuleOfWeekResponse } from "../../types/models/RuleOfWeekResponse";
import { getParagraphMetadata } from "../../utils/RuleUtil";
import {
  filterOutComponentForkParagraphs,
  getModuleNameFromTaxonomy,
  getParagraphIdFromIndex,
} from "../../utils/RulesOfTheWeekUtils";
import ParagraphSelector from "./ParagraphSelector";

interface Props {
  isOpen: boolean;
  mode: string;
  setIsOpen: (isOpen: boolean) => void;
  ruleModules: string[];
  getContentsFromModule: (moduleName: string) => void;
  ruleContents: {
    header: string;
    contents: { name: string; id: string }[];
  }[];
  getRulesFromContent: (contentId: string) => void;
  rules: RuleInstance[];
  ruleUnitState: RuleUnitState;
  editRecord?: RuleOfWeekResponse;
  onSaved: () => void;
}

export const RuleOfTheWeekDialog = ({
  isOpen,
  mode,
  setIsOpen,
  ruleModules,
  getContentsFromModule,
  ruleContents,
  getRulesFromContent,
  rules,
  ruleUnitState,
  editRecord,
  onSaved,
}: Props) => {
  const dispatch = useDispatch();

  const [displayName, setDisplayName] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [module, setModule] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [rule, setRule] = useState<string>("");
  const [ruleContent, setRuleContent] = useState<RuleInstance>();
  const [selectedParagraphIndex, setSelectedParagraphIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // errors
  const [displayNameError, setDisplayNameError] = useState<boolean>(false);
  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [moduleError, setModuleError] = useState<boolean>(false);
  const [contentError, setContentError] = useState<boolean>(false);
  const [ruleError, setRuleError] = useState<boolean>(false);
  const [selectedParagraphError, setSelectedParagraphError] = useState<boolean>(false);

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    setStartDateError(false);
    if (date && endDate && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      date.setHours(23, 59, 59, 0);
    }
    setEndDate(date);
    if (date && startDate && startDate > date) {
      setStartDate(null);
    }
  };

  const handleModuleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModule(e.target.value);
    setModuleError(false);
    setContent("");
    setRule("");
    setRuleContent(undefined);
    setSelectedParagraphIndex(null);
    getContentsFromModule(e.target.value);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
    setContentError(false);
    setRule("");
    setRuleContent(undefined);
    setSelectedParagraphIndex(null);
    getRulesFromContent(e.target.value);
  };

  const handleRuleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRule(e.target.value);
    setRuleError(false);
    // Select the first paragraph by default because it is always selectable.
    // It uses RuleId as the paragraphId, and RuleId is always present.
    setSelectedParagraphIndex(0);
    setSelectedParagraphError(false);

    const selectedRule = rules.find((rule) => rule.DisplayName === e.target.value);
    if (selectedRule) {
      setRuleContent(selectedRule);
    }
  };

  const handleParagraphSelect = (index: number) => {
    setSelectedParagraphIndex(index);
    setSelectedParagraphError(false);
  };

  // Set Edit Mode Values - Part 1: when dialog opens, set all values based on editRecord
  useEffect(() => {
    if (!isOpen || mode !== "edit" || !editRecord) return;

    setDisplayName(editRecord.DisplayName);
    setStartDate(new Date(editRecord.ROTWStart));
    setEndDate(editRecord.ROTWEnd ? new Date(editRecord.ROTWEnd) : null);

    const moduleName = getModuleNameFromTaxonomy(editRecord.TaxonomyTitle ?? "");
    setModule(moduleName);
    getContentsFromModule(moduleName);

    // Find ContentRevisionId by matching ContentDisplayName in ruleUnitState
    let contentId = "";
    ruleUnitState.baseContentRevisions?.forEach((revisions) => {
      const match = revisions.find((r) => r.DisplayName === editRecord.ContentDisplayName);
      if (match) contentId = match.ContentRevisionId;
    });
    if (contentId) {
      setContent(contentId);
      getRulesFromContent(contentId);
    }

    setRule(editRecord.RuleDisplayName);
  }, [isOpen, editRecord]);

  // Set Edit Mode Values - Part 2: once rules are loaded, set the ruleContent based on editRecord.RuleInstanceId
  useEffect(() => {
    if (mode !== "edit" || !editRecord || rules.length === 0 || ruleContent) return;
    const matched = rules.find((r) => r.RuleInstanceId === editRecord.RuleInstanceId);
    if (matched) setRuleContent(matched);
  }, [rules]);

  // Set Edit Mode Values - Part 3: once ruleContent is set, auto-select the matching paragraph
  useEffect(() => {
    if (mode !== "edit" || !editRecord || !ruleContent || selectedParagraphIndex !== null) return;
    if (editRecord.ParagraphId === ruleContent.RuleId) {
      // If the paragraphId matches the RuleId, select the first paragraph (index 0)
      setSelectedParagraphIndex(0);
      return;
    }
    const paragraphs = filterOutComponentForkParagraphs(ruleContent.Paragraphs ?? []);
    const idx =
      paragraphs.findIndex((p) => {
        const meta = getParagraphMetadata(p.Metadata ?? "");
        return meta?.id?.[0] === editRecord.ParagraphId;
      }) ?? -1;
    if (idx >= 0) setSelectedParagraphIndex(idx);
  }, [ruleContent]);

  const clearState = () => {
    setDisplayName("");
    setStartDate(null);
    setEndDate(null);
    setModule("");
    setContent("");
    setRule("");
    setSelectedParagraphIndex(null);
    setDisplayNameError(false);
    setStartDateError(false);
    setModuleError(false);
    setContentError(false);
    setRuleError(false);
    setSelectedParagraphError(false);
    setRuleContent(undefined);
  };

  const closeDialog = () => {
    clearState();
    setIsOpen(false);
  };

  const validateValues = () => {
    let result = true;
    if (!displayName.trim()) {
      setDisplayNameError(true);
      result = false;
    }
    if (!startDate) {
      setStartDateError(true);
      result = false;
    }
    if (!module) {
      setModuleError(true);
      result = false;
    }
    if (!content) {
      setContentError(true);
      result = false;
    }
    if (!rule) {
      setRuleError(true);
      result = false;
    }
    if (selectedParagraphIndex === null) {
      setSelectedParagraphError(true);
      result = false;
    }

    return result;
  };

  const saveChanges = () => {
    if (!validateValues()) {
      return;
    }

    if (selectedParagraphIndex === null) return;
    if (ruleContent === undefined) return;

    const paragraphId = getParagraphIdFromIndex(ruleContent, selectedParagraphIndex);
    if (!paragraphId) {
      setSelectedParagraphError(true);
      return;
    }

    const ruleOfTheWeek: RuleOfWeekRequest = {
      DisplayName: displayName,
      ROTWStart: startDate?.toISOString() ?? "",
      ROTWEnd: endDate?.toISOString(),
      RuleInstanceId: ruleContent?.RuleInstanceId ?? "",
      ParagraphId: paragraphId,
    };

    setIsSaving(true);
    if (mode === "edit" && editRecord) {
      dispatch(
        updateAdminRuleOfWeek(editRecord.Id, ruleOfTheWeek, (result) => {
          setIsSaving(false);
          if (result) {
            dispatch(showMessage("The rule of the week has been updated successfully."));
            onSaved();
            closeDialog();
          }
        })
      );
    } else {
      dispatch(
        createAdminRuleOfWeek(ruleOfTheWeek, (result) => {
          setIsSaving(false);
          if (result) {
            dispatch(showMessage("The rule of the week has been saved successfully."));
            onSaved();
            closeDialog();
          }
        })
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppDialog
        open={isOpen}
        title={
          <Typography sx={{ mb: 1, fontWeight: Fonts.SEMI_BOLD }}>
            {mode === "create" ? "Create Rule of the Week" : "Edit Rule of the Week"}
          </Typography>
        }
        onClose={closeDialog}
        maxWidth="lg"
        footer={
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={isSaving}
              sx={{ mr: 2, mb: 1, textTransform: "none", color: "white" }}
              onClick={saveChanges}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              sx={{ mr: 2, mb: 1, textTransform: "none" }}
              onClick={closeDialog}
            >
              Cancel
            </Button>
          </Box>
        }
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Box sx={{ display: "flex", gap: 3, minHeight: 500 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                required
                fullWidth
                label="Display Name"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  setDisplayNameError(false);
                }}
                error={displayNameError}
                helperText={displayNameError ? "Display name is required" : ""}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <DatePicker
                  label="Start Date*"
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "medium",
                      error: startDateError,
                      helperText: startDateError ? "Start date is required" : "",
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="dd/MM/yyyy"
                  minDate={startDate as Date}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "medium",
                    },
                  }}
                />
              </Box>
              <FormControl fullWidth>
                <TextField
                  select
                  required
                  value={module}
                  label="Module"
                  onChange={handleModuleChange}
                  error={moduleError}
                  helperText={moduleError ? "Module is required" : ""}
                >
                  {ruleModules.map((mod) => (
                    <MenuItem key={mod} value={mod}>
                      {mod}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  select
                  required
                  value={content}
                  label="Content"
                  onChange={handleContentChange}
                  disabled={!module}
                  error={contentError}
                  helperText={contentError ? "Content is required" : ""}
                >
                  {ruleContents.flatMap((rc, index) => [
                    <ListSubheader key={`header-${index}`} sx={{ color: "grey" }}>
                      {rc.header}
                    </ListSubheader>,
                    ...rc.contents.map((cont) => (
                      <MenuItem key={cont.id} value={cont.id}>
                        {cont.name}
                      </MenuItem>
                    )),
                  ])}
                </TextField>
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  select
                  required
                  value={rule}
                  label="Rule"
                  onChange={handleRuleChange}
                  disabled={!module || !content}
                  error={ruleError}
                  helperText={ruleError ? "Rule is required" : ""}
                >
                  {rules.map((rule) => (
                    <MenuItem key={rule.RuleId} value={rule.DisplayName}>
                      {rule.DisplayName.split(".")[1]?.trim() || rule.DisplayName}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Box>

            {/* Paragraph selection */}
            <ParagraphSelector
              ruleContent={ruleContent}
              selectedParagraphIndex={selectedParagraphIndex}
              hasError={selectedParagraphError}
              onSelect={handleParagraphSelect}
            />
          </Box>
        </Box>
      </AppDialog>
    </LocalizationProvider>
  );
};
